import { Controller } from "@hotwired/stimulus";
import SlimSelect from "slim-select";

export default class extends Controller {
  static targets = [
    "filterSelect",
    "talentMatchingTypeSelect",
    "toggleFilterCheckbox",
    "submitButton",
  ];

  static classes = ["hidden", "talentMatchingTypeSelect"];

  static values = {
    maxConcurrentFilters: Number,
  };

  connect() {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.classList.add(this.hiddenClass);
    }
  }

  talentMatchingTypeSelectTargetConnected(target) {
    target.classList.add(...this.talentMatchingTypeSelectClasses);

    // Marks the first option as the placeholder.
    // eslint-disable-next-line no-param-reassign
    target.querySelector("option:first-child").dataset.placeholder = "true";

    // Sets HTML content to each option with a `span` tag and assigns a class
    // to the span based on the option value to eventually show the talent
    // matching type badge.
    // This is a global class that is referenced in the CSS module.
    // There is no way to set an arbitrary data attribute on the select
    // options from here, which is carried forward by SlimSelect.
    // The alternative would be to set 5 classes on the controller for each
    // status, which seemed excessive.
    target.querySelectorAll("option").forEach((option) => {
      if (option.value && option.value.endsWith("talent_matching")) {
        const optionText = option.innerHTML;
        const baseOptionHtml = optionText.split("-")[0].trim();
        // eslint-disable-next-line no-param-reassign
        option.dataset.html = `${baseOptionHtml} <span class="talent-matching-type-select-option-badge talent-matching-type-select-option-${option.value}"></span>`;
      }
    });

    // Prevents the select from being translated by Google Translate.
    // On this page, when the select gets translated, the SlimSelect library
    // will change the DOM for it which will trigger a new translation by
    // Google Translate, which will change the DOM again, and so on.
    // Checking the value of the select in the submit method is not enough to
    // get the translation to work...
    target.classList.add("notranslate");

    // Disables the select if its parent fieldset is disabled, so the
    // disabled state gets reflected in SlimSelect.
    const isDisabled = Boolean(
      target.disabled || target.closest("fieldset[disabled]"),
    );

    this.talentMatchingTypeSelect = new SlimSelect({
      select: target,
      settings: {
        allowDeselect: true,
        disabled: isDisabled,
        showSearch: false,
      },
    });
  }

  /**
   * Checks if the number of currently checked toggle filter checkboxes is at
   * or over the limit defined by `maxConcurrentFiltersValue`,
   */
  get filterLimitReached() {
    const count = this.toggleFilterCheckboxTargets.reduce(
      (acc, checkbox) => acc + (checkbox.checked ? 1 : 0),
      0,
    );
    return count >= this.maxConcurrentFiltersValue;
  }

  /**
   * Disables newly connected filter checkboxes when the limit of concurrent
   * filters is reached.
   */
  toggleFilterCheckboxTargetConnected(target) {
    if (this.filterLimitReached) {
      // eslint-disable-next-line no-param-reassign
      target.disabled = !target.checked;
    }
  }

  /**
   * Updates the URL from the filter form submission so the URL params match.
   */
  // eslint-disable-next-line class-methods-use-this
  updateURLFromFormSubmit({ target }) {
    const { href } = window.location;
    const url = new URL(href);

    const formData = new FormData(target);

    url.search = new URLSearchParams(formData).toString();
    const to = url.href.replace(url.origin, "");
    window.history.pushState({}, "", to);
  }

  /**
   * Submits the filter form using its submit button.
   */
  submit() {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.click();
    }
  }

  /**
   * Toggles a filter to be visible or hidden based on the checkbox state.
   *
   * If a filter is being removed and it has a selection, itself value is
   * cleared and the filter form is submitted so the filter is removed.
   */
  handleToggleFilterCheckboxChange(event) {
    const {
      currentTarget: { checked, name },
    } = event;

    const select = this.filterSelectTargets.find((s) => s.name === name);

    select
      ?.closest(".field-wrapper")
      .classList.toggle(this.hiddenClass, !checked);

    // Adjust the value and submits the form when a filter with a selection is
    // being removed.
    if (!checked && select) {
      const nullIndex = select.multiple ? -1 : 0;
      const hasSelection = select.selectedIndex > nullIndex;
      if (hasSelection) {
        select.selectedIndex = nullIndex;
        this.submit();
      }
    }

    // Disables the remaining unchecked filter checkboxes when the limit of
    // concurrent filters is reached.
    const atLimit = this.filterLimitReached;
    this.toggleFilterCheckboxTargets.forEach((checkbox) => {
      // eslint-disable-next-line no-param-reassign
      checkbox.disabled = !checkbox.checked && atLimit;
    });
  }

  /**
   * Clears the value of "ordering" when the form is being submitted via enter
   * on the search field, so the default ordering for search is relevance,
   * which is implied.
   *
   * The user can still change the ordering after the search is submitted, by
   * explicitly selecting a new ordering.
   */
  // eslint-disable-next-line class-methods-use-this
  submitSearch(event) {
    const {
      target: { form },
    } = event;
    form.elements.ordering.value = "";
  }

  /**
   * Submits the filter form on `search` events, which is what is emitted when
   * the user clicks the "x" to clear a search field.
   */
  clearSearch(event) {
    const {
      currentTarget: { value },
    } = event;
    if (value === "") {
      this.submit();
    }
  }

  talentMatchingTypeSelectTargetDisconnected() {
    if (this.talentMatchingTypeSelect) {
      this.talentMatchingTypeSelect.destroy();
    }
  }
}
